export const articleContent = [
    {
        id: 'menstrual-hygiene-day',
        title: 'Menstrual Hygiene Day',
        category: 'Menstrual Health',
        readTime: '5 min',
        imageUrl: '/menstrual-hygiene-day.jpg',
        excerpt:
            'May 28th is celebrated as Menstrual Hygiene Day worldwide, aiming to break taboos and raise awareness about menstrual hygiene management.',
        content: `
      <p>May 28th is celebrated as Menstrual Hygiene Day worldwide. First celebrated in 2014, this day aims to break taboos surrounding menstruation and raise awareness about the importance of menstrual hygiene management for women and girls worldwide.</p>

      <h2>The Importance of Menstrual Hygiene</h2>
      <p>Good menstrual hygiene is crucial for the health, well-being, and dignity of women and girls. Poor menstrual hygiene can lead to reproductive tract infections, urinary tract infections, and other health issues. Additionally, the stigma surrounding menstruation can lead to:</p>
      <ul>
        <li>Missed educational opportunities when girls avoid school during their periods</li>
        <li>Workplace absenteeism</li>
        <li>Restricted participation in community activities</li>
        <li>Psychological impact including shame and low self-esteem</li>
      </ul>

      <h2>Challenges in Menstrual Hygiene Management</h2>
      <p>Many women and girls around the world face significant challenges in managing their menstruation with dignity:</p>
      <ul>
        <li><strong>Limited access to products:</strong> Millions cannot afford or access clean menstrual products</li>
        <li><strong>Inadequate sanitation facilities:</strong> Lack of clean water, private toilets, and waste disposal options</li>
        <li><strong>Knowledge gaps:</strong> Limited education about menstruation and proper hygiene practices</li>
        <li><strong>Cultural taboos:</strong> Restrictions and stigma that impact women's daily lives during menstruation</li>
      </ul>

      <h2>Progress and Initiatives</h2>
      <p>In recent years, there has been growing recognition of the importance of menstrual hygiene:</p>
      <ul>
        <li>Many countries have removed taxes on menstrual products</li>
        <li>Schools are increasingly providing free menstrual products to students</li>
        <li>Organizations worldwide distribute menstrual products to vulnerable populations</li>
        <li>Educational programs are being implemented to increase awareness</li>
        <li>Innovative, sustainable menstrual products are being developed and distributed</li>
      </ul>

      <h2>How You Can Contribute</h2>
      <p>Everyone can play a role in breaking taboos and improving menstrual hygiene:</p>
      <ul>
        <li>Speak openly about menstruation to break the silence and stigma</li>
        <li>Support organizations working on menstrual hygiene initiatives</li>
        <li>Advocate for policies that make menstrual products more accessible</li>
        <li>Educate the next generation about menstrual health</li>
        <li>Consider using sustainable menstrual products to reduce environmental impact</li>
      </ul>

      <p>Menstrual Hygiene Day reminds us that menstruation is a natural process that should not limit anyone's potential. By improving menstrual hygiene management globally, we can help ensure that women and girls can participate fully in society, with dignity and confidence, every day of the month.</p>
    `,
    },
    {
        id: 'endometriosis',
        title: 'Endometriosis: Understanding the Invisible Condition',
        category: 'Reproductive Health',
        readTime: '7 min',
        imageUrl: '/endometriosis.webp',
        excerpt:
            'Endometriosis affects approximately 1 in 10 women of reproductive age, yet it remains widely misunderstood and often misdiagnosed.',
        content: `
      <p>Endometriosis affects approximately 1 in 10 women of reproductive age worldwide, yet it remains widely misunderstood and often misdiagnosed. This condition occurs when tissue similar to the lining of the uterus (endometrium) grows outside the uterus, commonly on the ovaries, fallopian tubes, and tissues lining the pelvis.</p>

      <h2>What Happens in Endometriosis?</h2>
      <p>During a normal menstrual cycle, the endometrium thickens, breaks down, and bleeds out of the body. In endometriosis, the displaced tissue acts just like normal endometrium — it thickens, breaks down, and bleeds with each menstrual cycle. However, because this tissue has no way to exit the body, it becomes trapped, leading to:</p>
      <ul>
        <li>Irritation of surrounding tissues</li>
        <li>Formation of scar tissue and adhesions</li>
        <li>Severe pain during periods</li>
        <li>Potential fertility problems</li>
      </ul>

      <h2>Common Symptoms</h2>
      <p>Symptoms of endometriosis can vary widely, and some women may have severe endometriosis with minimal symptoms while others with milder cases experience debilitating pain. Common symptoms include:</p>
      <ul>
        <li>Painful periods (dysmenorrhea) with pain far worse than typical menstrual cramps</li>
        <li>Chronic pelvic pain</li>
        <li>Pain during or after sexual intercourse</li>
        <li>Painful bowel movements or urination, especially during periods</li>
        <li>Excessive bleeding during periods or between periods</li>
        <li>Fatigue, diarrhea, constipation, bloating, or nausea during menstrual periods</li>
        <li>Difficulty conceiving</li>
      </ul>

      <h2>Diagnosis Challenges</h2>
      <p>Endometriosis can be difficult to diagnose because:</p>
      <ul>
        <li>Symptoms can resemble other conditions like irritable bowel syndrome or pelvic inflammatory disease</li>
        <li>Many healthcare providers lack awareness about the condition</li>
        <li>Patients' pain is sometimes normalized or dismissed</li>
        <li>Definitive diagnosis requires laparoscopic surgery to visualize and biopsy the tissue</li>
      </ul>
      <p>On average, women may wait 7-10 years from symptom onset to diagnosis, which can significantly impact quality of life and fertility.</p>

      <h2>Treatment Options</h2>
      <p>While there is no cure for endometriosis, several treatment approaches can help manage symptoms:</p>
      <ul>
        <li><strong>Pain medication:</strong> Over-the-counter or prescription pain relievers</li>
        <li><strong>Hormone therapy:</strong> Birth control pills, progestin therapy, or GnRH agonists to reduce estrogen production</li>
        <li><strong>Conservative surgery:</strong> Laparoscopic removal of endometriosis tissue while preserving the uterus and ovaries</li>
        <li><strong>Hysterectomy:</strong> In severe cases, surgical removal of the uterus, cervix, and sometimes ovaries</li>
        <li><strong>Complementary approaches:</strong> Acupuncture, dietary changes, physical therapy, and exercise</li>
      </ul>

      <h2>Living with Endometriosis</h2>
      <p>If you suspect you have endometriosis or have been diagnosed:</p>
      <ul>
        <li>Find a healthcare provider who specializes in endometriosis</li>
        <li>Keep a detailed record of your symptoms to help with diagnosis and treatment</li>
        <li>Consider joining a support group to connect with others who understand</li>
        <li>Explore stress reduction techniques like yoga or meditation</li>
        <li>Advocate for yourself if you feel your pain is being dismissed</li>
      </ul>

      <p>While endometriosis can be challenging to live with, increased awareness, earlier diagnosis, and advances in treatment options are helping many women manage their symptoms and improve their quality of life. If you experience severe period pain that interferes with daily activities, don't dismiss it as normal—talk to your healthcare provider about the possibility of endometriosis.</p>
    `,
    },
    {
        id: 'myths-about-menstruation',
        title: 'Myths About Menstruation We Need to Stop Believing',
        category: 'Menstrual Health',
        readTime: '6 min',
        imageUrl: '/myths-about-menstruation.webp',
        excerpt:
            'Despite being a natural biological process, menstruation remains shrouded in myths and misconceptions that perpetuate stigma.',
        content: `
      <p>Despite being a natural biological process that affects half the world's population, menstruation remains shrouded in myths, misconceptions, and taboos. These myths not only perpetuate stigma but can also lead to harmful practices. Let's debunk some common menstrual myths and replace them with facts.</p>

      <h2>Myth 1: Menstruating Women Are Impure</h2>
      <p><strong>The myth:</strong> In many cultures, menstruating women are considered unclean or impure. They may be prohibited from entering religious spaces, cooking, touching certain foods, or participating in normal activities.</p>
      <p><strong>The fact:</strong> Menstruation is a normal, healthy biological process. There is absolutely nothing impure or unclean about a menstruating woman. These taboos are based on superstition rather than science and can be harmful to women's psychological well-being and social status.</p>

      <h2>Myth 2: Exercise Makes Periods Worse</h2>
      <p><strong>The myth:</strong> Women should avoid physical activity during their period because it will increase pain or flow.</p>
      <p><strong>The fact:</strong> Exercise can actually help alleviate menstrual discomfort for many women. Physical activity releases endorphins that act as natural painkillers. Exercise can also improve circulation, which may reduce bloating and cramping. Of course, each person should listen to their body and adjust activity levels as needed.</p>

      <h2>Myth 3: You Shouldn't Bathe During Your Period</h2>
      <p><strong>The myth:</strong> Bathing during menstruation can lead to increased flow, infertility, or other health problems.</p>
      <p><strong>The fact:</strong> Regular bathing during menstruation is essential for hygiene and can help reduce odor and the risk of infection. Warm baths or showers can even help relieve menstrual cramps. There is no medical reason to avoid bathing during periods.</p>

      <h2>Myth 4: Premenstrual Mood Changes Are All in Your Head</h2>
      <p><strong>The myth:</strong> Women exaggerate premenstrual symptoms, and mood changes are psychological rather than physical.</p>
      <p><strong>The fact:</strong> Premenstrual syndrome (PMS) is a real medical condition caused by hormonal changes. For some women, these changes can trigger significant physical and emotional symptoms. Severe cases, known as Premenstrual Dysphoric Disorder (PMDD), can be debilitating and require medical treatment.</p>

      <h2>Myth 5: You Can't Get Pregnant During Your Period</h2>
      <p><strong>The myth:</strong> It's impossible to get pregnant while menstruating.</p>
      <p><strong>The fact:</strong> While the chance is lower, pregnancy can occur if you have sex during your period, especially for women with shorter menstrual cycles. Sperm can survive in the female reproductive tract for up to 5 days, so if ovulation occurs shortly after your period ends, pregnancy is possible from sex during menstruation.</p>

      <h2>Myth 6: Irregular Periods Always Indicate a Problem</h2>
      <p><strong>The myth:</strong> All healthy women have regular, predictable periods, and irregularity always signals a health issue.</p>
      <p><strong>The fact:</strong> Menstrual cycles can naturally vary. Factors like stress, travel, diet, exercise, and age can all affect cycle length and flow. While persistent irregularity should be discussed with a healthcare provider, not all variations indicate a problem.</p>

      <h2>Myth 7: Menstrual Pain Is Normal and Should Be Endured</h2>
      <p><strong>The myth:</strong> Severe menstrual pain is just part of being a woman and should be tolerated.</p>
      <p><strong>The fact:</strong> While mild to moderate cramping is common, severe pain that interferes with daily activities is not normal and could indicate conditions like endometriosis or fibroids. Women should not hesitate to seek medical help for debilitating period pain.</p>

      <h2>Moving Forward: Education Is Key</h2>
      <p>Breaking down menstrual myths requires open, fact-based education for people of all genders and ages. By challenging these misconceptions, we can:</p>
      <ul>
        <li>Reduce stigma and shame around menstruation</li>
        <li>Improve access to menstrual products and proper facilities</li>
        <li>Ensure better health outcomes by encouraging women to seek help when needed</li>
        <li>Create more inclusive environments where natural bodily functions aren't taboo</li>
      </ul>

      <p>Next time you hear a menstrual myth, take the opportunity to share facts and help build a world where periods are understood as the normal biological process they are.</p>
    `,
    },
    {
        id: 'menstrual-cup',
        title: 'The Complete Guide to Menstrual Cups',
        category: 'Periods',
        readTime: '7 min',
        imageUrl: '/menstrual-cup.webp',
        excerpt:
            'Menstrual cups are a sustainable, cost-effective alternative to traditional period products. Learn everything about choosing and using them.',
        content: `
      <p>Menstrual cups have gained popularity as a sustainable, cost-effective alternative to traditional period products. These flexible cups made of medical-grade silicone, latex, or thermoplastic elastomer (TPE) are inserted into the vagina to collect menstrual flow rather than absorb it.</p>

      <h2>Benefits of Menstrual Cups</h2>
      <p>There are numerous reasons why many people are making the switch to menstrual cups:</p>
      <ul>
        <li><strong>Cost-effective:</strong> A single cup costs $20-40 but can last up to 10 years with proper care, saving thousands compared to disposable products</li>
        <li><strong>Eco-friendly:</strong> Significantly reduces menstrual waste, with one cup replacing approximately 3,000 tampons over its lifetime</li>
        <li><strong>Long-lasting protection:</strong> Can be worn for up to 12 hours, depending on flow</li>
        <li><strong>Reduced risk of TSS:</strong> Lower risk of Toxic Shock Syndrome compared to tampons</li>
        <li><strong>Body-friendly:</strong> Doesn't dry the vagina or disrupt its natural pH and microbiome</li>
        <li><strong>Odor control:</strong> Since blood only has an odor when exposed to air, cups typically reduce menstrual odor</li>
        <li><strong>Activity-friendly:</strong> Suitable for swimming, sleeping, and vigorous physical activities</li>
      </ul>

      <h2>Choosing the Right Cup</h2>
      <p>Menstrual cups come in different sizes and firmness levels. Consider these factors when selecting your cup:</p>
      <ul>
        <li><strong>Age and childbirth history:</strong> Younger people or those who haven't given birth vaginally typically start with smaller cups</li>
        <li><strong>Cervix height:</strong> During your period, check where your cervix sits—low, medium, or high—and choose a cup length accordingly</li>
        <li><strong>Flow volume:</strong> Heavier flows may require larger capacity cups</li>
        <li><strong>Physical activity:</strong> Active people may need firmer cups that stay in place better</li>
        <li><strong>Pelvic floor strength:</strong> Those with stronger pelvic floor muscles may find softer cups more comfortable</li>
      </ul>

      <h2>Insertion and Removal Techniques</h2>
      <p>There's a learning curve to using menstrual cups, but most people become comfortable within 2-3 cycles. Here are the basic techniques:</p>

      <h3>Insertion Methods:</h3>
      <ol>
        <li><strong>C-fold:</strong> Fold the cup in half to form a C shape, then insert</li>
        <li><strong>Punch-down fold:</strong> Push one side of the rim down into the cup to create a smaller insertion point</li>
        <li><strong>7-fold:</strong> Fold the cup in half, then fold one corner down to form a "7" shape</li>
      </ol>

      <h3>Removal Steps:</h3>
      <ol>
        <li>Wash hands thoroughly</li>
        <li>Relax and squat slightly to bring the cup lower</li>
        <li>Insert fingers until you can reach the base of the cup</li>
        <li>Pinch the base to break the suction seal</li>
        <li>Gently rock the cup side to side while pulling downward</li>
        <li>Keep the cup upright to avoid spills</li>
      </ol>

      <h2>Cleaning and Maintenance</h2>
      <p>Proper care ensures your cup remains safe and lasts its full lifespan:</p>
      <ul>
        <li><strong>During your cycle:</strong> Rinse with clean water and mild, unscented soap every 12 hours or when emptied</li>
        <li><strong>Between periods:</strong> Sterilize by boiling in water for 3-5 minutes or using a sterilizing solution made specifically for menstrual cups</li>
        <li><strong>Storage:</strong> Keep in the breathable cotton bag that came with your cup, never in an airtight container</li>
        <li><strong>Public bathrooms:</strong> Carry a water bottle to rinse, or simply wipe with toilet paper and rinse properly later</li>
      </ul>

      <h2>Common Challenges and Solutions</h2>
      <p>If you're new to cups, you might encounter these issues:</p>
      <ul>
        <li><strong>Leaking:</strong> Usually caused by improper placement or not creating a good seal—try reinserting or a different fold method</li>
        <li><strong>Difficulty removing:</strong> Never pull on the stem alone; always break the suction seal first by pinching the base</li>
        <li><strong>Discomfort:</strong> May indicate the wrong size or firmness—consider trying a different cup</li>
        <li><strong>Staining:</strong> Normal and doesn't indicate the cup needs replacement; soaking in diluted hydrogen peroxide can help</li>
      </ul>

      <h2>Is a Menstrual Cup Right for You?</h2>
      <p>While menstrual cups have many advantages, they're not for everyone. Consider your lifestyle, comfort with your body, and personal preferences. Many cup users report a learning curve but ultimately find cups to be life-changing once mastered.</p>

      <p>If you're interested in trying a menstrual cup, research different brands, read reviews, and consider starting during a lighter flow day when you have time to practice insertion and removal without pressure.</p>
    `,
    },
    {
        id: 'premenstrual-syndrome',
        title: 'Understanding and Managing Premenstrual Syndrome (PMS)',
        category: 'Menstrual Health',
        readTime: '6 min',
        imageUrl: '/premenstrual-syndrome.webp',
        excerpt:
            'PMS refers to physical, emotional, and behavioral symptoms many women experience before their period. Learn how to manage them effectively.',
        content: `
      <p>Premenstrual Syndrome (PMS) refers to a combination of physical, emotional, and behavioral symptoms that many women experience in the days or weeks leading up to their menstrual period. While exact causes remain unclear, hormonal fluctuations play a significant role, with symptoms typically improving once menstruation begins.</p>

      <h2>Common PMS Symptoms</h2>
      <p>PMS can manifest differently for each person and may include:</p>

      <h3>Physical Symptoms:</h3>
      <ul>
        <li>Bloating and water retention</li>
        <li>Breast tenderness or swelling</li>
        <li>Headaches or migraines</li>
        <li>Fatigue and sleep disturbances</li>
        <li>Food cravings, especially for sweets or carbohydrates</li>
        <li>Digestive issues like constipation or diarrhea</li>
        <li>Joint or muscle pain</li>
        <li>Acne flare-ups</li>
      </ul>

      <h3>Emotional and Behavioral Symptoms:</h3>
      <ul>
        <li>Mood swings, irritability, or anger</li>
        <li>Anxiety or tension</li>
        <li>Depression or feelings of sadness</li>
        <li>Difficulty concentrating</li>
        <li>Social withdrawal</li>
        <li>Changes in libido</li>
        <li>Crying spells</li>
        <li>Feeling overwhelmed</li>
      </ul>

      <h2>When PMS Becomes PMDD</h2>
      <p>For some women, symptoms are severe enough to significantly interfere with daily life. This condition, called Premenstrual Dysphoric Disorder (PMDD), affects about 3-8% of women and is characterized by more intense emotional and behavioral symptoms. PMDD is now recognized as a medical condition in the Diagnostic and Statistical Manual of Mental Disorders (DSM-5) and may require more specialized treatment.</p>

      <h2>Managing PMS: Lifestyle Approaches</h2>
      <p>Many women find relief from PMS symptoms through lifestyle modifications:</p>

      <h3>Dietary Changes:</h3>
      <ul>
        <li><strong>Eat smaller, more frequent meals</strong> to maintain stable blood sugar levels</li>
        <li><strong>Reduce salt intake</strong> to minimize bloating</li>
        <li><strong>Limit caffeine and alcohol</strong>, which can affect mood and sleep</li>
        <li><strong>Increase calcium-rich foods</strong> like dairy or fortified plant milks</li>
        <li><strong>Stay hydrated</strong> despite the counterintuitive nature of combating water retention</li>
        <li><strong>Choose complex carbohydrates</strong> over simple sugars</li>
      </ul>

      <h3>Physical Activity:</h3>
      <ul>
        <li>Aim for at least 30 minutes of moderate exercise most days</li>
        <li>Consider yoga, which has been shown to reduce PMS symptoms in some studies</li>
        <li>Try gentle walks when energy is low rather than skipping activity entirely</li>
      </ul>

      <h3>Stress Management:</h3>
      <ul>
        <li>Practice meditation or mindfulness techniques</li>
        <li>Try deep breathing exercises during moments of tension</li>
        <li>Consider cognitive behavioral therapy approaches</li>
        <li>Prioritize activities that bring joy and relaxation</li>
      </ul>

      <h3>Sleep Hygiene:</h3>
      <ul>
        <li>Maintain a consistent sleep schedule</li>
        <li>Create a cool, dark, comfortable sleeping environment</li>
        <li>Avoid screens before bedtime</li>
        <li>Consider a warm bath before bed to improve sleep quality</li>
      </ul>

      <h2>Medical Approaches to PMS</h2>
      <p>When lifestyle modifications aren't enough, these medical treatments may help:</p>
      <ul>
        <li><strong>Over-the-counter pain relievers:</strong> NSAIDs like ibuprofen can reduce cramping, headaches, and breast tenderness</li>
        <li><strong>Hormonal birth control:</strong> Can stabilize hormone fluctuations and reduce physical symptoms</li>
        <li><strong>Antidepressants:</strong> Selective serotonin reuptake inhibitors (SSRIs) may help with emotional symptoms, especially for PMDD</li>
        <li><strong>Diuretics:</strong> May be prescribed for severe bloating and water retention</li>
        <li><strong>Anti-anxiety medications:</strong> Sometimes used for short-term management of severe anxiety symptoms</li>
      </ul>

      <h2>Complementary Approaches</h2>
      <p>Some women find these complementary approaches helpful, though scientific evidence varies:</p>
      <ul>
        <li><strong>Supplements:</strong> Calcium, magnesium, vitamin B6, and chaste tree berry (vitex) have shown promise in some studies</li>
        <li><strong>Acupuncture:</strong> May help reduce symptoms for some women</li>
        <li><strong>Herbal remedies:</strong> Such as evening primrose oil or St. John's Wort (caution: may interact with medications)</li>
        <li><strong>Heat therapy:</strong> Heating pads can relieve muscle tension and cramps</li>
      </ul>

      <h2>Tracking Your Symptoms</h2>
      <p>Keeping a symptom diary for 2-3 months can help you:</p>
      <ul>
        <li>Identify patterns in your symptoms</li>
        <li>Recognize triggers that worsen symptoms</li>
        <li>Evaluate which management strategies are most effective</li>
        <li>Provide valuable information if you consult a healthcare provider</li>
      </ul>

      <p>While PMS is common, debilitating symptoms are not something you should have to "just live with." If PMS significantly affects your quality of life, consider discussing your symptoms with a healthcare provider who can help you develop an effective management plan tailored to your needs.</p>
    `,
    },
];
